import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';

/**
 * SegmentCaptureModal
 *
 * Confirms a retroactively captured segment. The coach presses one control
 * after a point ends and lands here with the preceding window already
 * selected, so the common path is Enter and back to playback. Adjustment is
 * available but never required.
 *
 * The local timeline is a fixed 60 second window rather than the whole video.
 * The main timeline maps the entire source across its width, which at a
 * 60 minute lesson puts a 15 second range under 5px and makes two handles
 * impossible to hit. Sixty seconds across the same width is roughly 19px per
 * second, so both handles are grabbable and there is no need for the minimum
 * width clamp the main timeline applies.
 */

const WINDOW_SECONDS = 60;
/** Shortest segment the handles may produce. */
const MIN_SEGMENT_SECONDS = 0.5;
/**
 * Seconds of video the window grows per second of holding a handle at the edge
 * of the track. Steady rather than accelerating: the coach has to be able to
 * stop where they mean to, and an easing curve makes the last second of a hold
 * unpredictable.
 */
const EXTEND_RATE = 10;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Whole seconds, in words. Display only: startTime and endTime stay fractional
 * and it is the fractional values that get saved.
 *
 * The handle constraint is a 0.5s minimum, and Math.round takes that to 1, so
 * this cannot print "0 seconds".
 *
 * Not "clip": in this codebase a clip is a recorded webm produced by the clip
 * editor. This is a segment, which is a range on the source video.
 */
function formatDuration(seconds: number): string {
  const whole = Math.round(seconds);
  // Rounded first, so 59.7 reads as "1 min" rather than "60 seconds".
  if (whole >= 60) {
    const mins = Math.floor(whole / 60);
    const secs = whole % 60;
    return secs === 0 ? `${mins} min` : `${mins} min ${secs} sec`;
  }
  return `${whole} ${whole === 1 ? 'second' : 'seconds'}`;
}

interface SegmentCaptureModalProps {
  /** Start of the captured range, already clamped to 0 by the caller. */
  captureStart: number;
  /** End of the captured range: the playhead at the moment of capture. */
  captureEnd: number;
  /** Full source duration, used only to clamp the local window. */
  duration: number;
  /** Prefilled name. Used as-is if the coach clears the field. */
  defaultName: string;
  /**
   * Seeks the source video. Wired to VideoEditorPlayer.seek(), which already
   * coalesces against remote decode latency, so this is called on every drag
   * frame without additional throttling.
   */
  onSeek: (time: number) => void;
  onSave: (startTime: number, endTime: number, label: string, notes?: string) => void;
  onCancel: () => void;
  /**
   * Where the coach last left the panel, or null to open at the default.
   * Held by the editor so it survives one capture to the next and resets when
   * the editor closes. Deliberately not localStorage.
   */
  position: { x: number; y: number } | null;
  onPositionChange: (pos: { x: number; y: number }) => void;
  /** Whether the organizer rail is showing, so the default clears it. */
  organizerOpen?: boolean;
  /**
   * The editor's controls bar. On a phone the panel docks against it, below
   * when it fits and above when it does not, and never over it; the bar is
   * also left uncovered by the scrim, so the main scrubber keeps working.
   */
  transportEl?: HTMLElement | null;
  /**
   * Phone: the viewing stage. The scrim leaves it uncovered, so pinch, pan
   * and the zoom Reset chip work while the panel is open (spec 11.2).
   */
  stageEl?: HTMLElement | null;
  /**
   * Phone placement (spec 1d). Landscape: the plan rail's slot at the right,
   * whether or not the plan is open. Portrait: the plan region under the
   * controls bar.
   */
  phoneDock?: { landscape: boolean; railWidth: number; insets: { top: number; right: number; bottom: number; left: number } };
}

/** Rail width at md and up. Matches LessonOrganizerPanel's `md:w-80`. */
const RAIL_W = 320;
/** Tailwind's md. Below it the rail stacks under the video instead of beside it. */
const MD = 768;
/** Clearance under the panel: the 56 transport row plus breathing room. */
const BOTTOM_GAP = 96;
/** Minimum distance from any viewport edge, used for the default and the clamp. */
const EDGE = 12;

export function SegmentCaptureModal({
  captureStart,
  captureEnd,
  duration,
  defaultName,
  onSeek,
  onSave,
  onCancel,
  position,
  onPositionChange,
  organizerOpen = false,
  transportEl = null,
  stageEl = null,
  phoneDock,
}: SegmentCaptureModalProps) {
  const isMobile = usePhoneLayout().phone;
  /** Phone: the panel is docked against the controls bar rather than floated. */
  const docked = isMobile && !!transportEl;
  /** The controls bar's rect while docked, for the panel and the scrim's gap. */
  const [bar, setBar] = useState<{ top: number; bottom: number } | null>(null);
  /** The stage's rect while docked: the hole in the scrim. */
  const [hole, setHole] = useState<{ left: number; top: number; right: number; bottom: number } | null>(null);
  /** Docked width and the most height the slot allows; the panel scrolls past it. */
  const [dockBox, setDockBox] = useState<{ w: number; maxH: number } | null>(null);
  const [start, setStart] = useState(captureStart);
  const [end, setEnd] = useState(captureEnd);
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState('');
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);

  const trackRef = useRef<HTMLDivElement>(null);
  /* Phone (spec 11.6): the track's width, so handles closer than a finger
     can be pushed apart. */
  const [trackW, setTrackW] = useState(0);
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setTrackW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const saveRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState<{ x: number; y: number } | null>(position);
  const [movingPanel, setMovingPanel] = useState(false);

  /** Keeps the panel wholly on screen. Shared by the default, the drag and the resize. */
  const clamp = useCallback((x: number, y: number) => {
    const el = panelRef.current;
    const w = el?.offsetWidth ?? 0;
    const h = el?.offsetHeight ?? 0;
    return {
      x: Math.max(EDGE, Math.min(x, window.innerWidth - w - EDGE)),
      y: Math.max(EDGE, Math.min(y, window.innerHeight - h - EDGE)),
    };
  }, []);

  // Default: low over the video and horizontally centred in whatever width the
  // organizer rail leaves. Measured rather than assumed, so the panel's own
  // height decides how far up from the transport row it sits.
  /* Docked (spec 1d). Portrait: in the plan region under the controls bar,
     never over the stage or the bar; if it is taller than the region it
     scrolls inside itself. Landscape: in the plan rail's slot at the right,
     under the header. Recomputed on resize, since the bar moves when the
     viewport does. */
  useLayoutEffect(() => {
    if (!docked || !transportEl) return;
    const place = () => {
      const el = panelRef.current;
      if (!el) return;
      const r = transportEl.getBoundingClientRect();
      setBar({ top: r.top, bottom: r.bottom });
      const sr = stageEl?.getBoundingClientRect();
      setHole(sr ? { left: sr.left, top: sr.top, right: sr.right, bottom: sr.bottom } : null);
      const ins = phoneDock?.insets ?? { top: 0, right: 0, bottom: 0, left: 0 };
      if (phoneDock?.landscape) {
        /* Spec 13.14: a glass panel over the video at the right, inside the
           one inset rule (safe area plus 12), between the top controls and
           the bottom pill (44 each, 8 clear of them). */
        const vw = window.visualViewport?.width ?? window.innerWidth;
        const vh = window.visualViewport?.height ?? window.innerHeight;
        const w = phoneDock.railWidth;
        const top = ins.top + 12 + 44 + 8;
        setDockBox({ w, maxH: vh - top - (ins.bottom + 12 + 44 + 8) });
        setPos({ x: vw - ins.right - 12 - w, y: top });
        return;
      }
      const w = window.innerWidth - ins.left - ins.right - 16;
      const top = r.bottom + 8;
      setDockBox({ w, maxH: window.innerHeight - top - ins.bottom - 8 });
      setPos({ x: ins.left + 8, y: top });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [docked, transportEl, phoneDock, stageEl]);

  useLayoutEffect(() => {
    if (pos || docked) return;
    const el = panelRef.current;
    if (!el) return;
    const availW = window.innerWidth - (organizerOpen && window.innerWidth >= MD ? RAIL_W : 0);
    setPos(clamp(
      (availW - el.offsetWidth) / 2,
      window.innerHeight - el.offsetHeight - BOTTOM_GAP,
    ));
  }, [pos, docked, organizerOpen, clamp]);

  // A remembered position can fall outside a smaller window.
  useEffect(() => {
    if (docked) return;
    const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp, docked]);

  /**
   * Panel drag, from the header only. Pointer events with capture, so the drag
   * survives the pointer leaving the header and needs no document listeners.
   * The range handles are separate elements with their own mousedown, so the
   * two gestures can never be confused for one another.
   */
  const handlePanelDragStart = useCallback((e: React.PointerEvent) => {
    // Docked on a phone: the panel's place is next to the controls bar.
    if (!pos || docked) return;
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    setMovingPanel(true);
    const dx = e.clientX - pos.x;
    const dy = e.clientY - pos.y;

    const move = (ev: PointerEvent) => setPos(clamp(ev.clientX - dx, ev.clientY - dy));
    const up = (ev: PointerEvent) => {
      setMovingPanel(false);
      el.releasePointerCapture(ev.pointerId);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      setPos((p) => { if (p) onPositionChange(p); return p; });
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
  }, [pos, docked, clamp, onPositionChange]);

  /**
   * Frozen at open. Dragging a handle seeks, seeking moves the editor's
   * currentTime, and deriving the window from that would slide the window out
   * from under the handle being dragged.
   */
  const initialWindow = useMemo<[number, number]>(() => {
    const centre = (captureStart + captureEnd) / 2;
    let lo = centre - WINDOW_SECONDS / 2;
    let hi = centre + WINDOW_SECONDS / 2;
    if (lo < 0) { hi += -lo; lo = 0; }
    if (hi > duration) { lo -= hi - duration; hi = duration; }
    return [Math.max(0, lo), Math.min(duration, hi)];
    // Deliberately keyed on the capture, not on start/end.
  }, [captureStart, captureEnd, duration]);

  /**
   * The window is state seeded by that computation rather than the computation
   * itself, because holding a handle at the edge of the track grows it. It is
   * still not derived from currentTime, which is what the note above is about.
   *
   * winRef mirrors it for the extend loop: a rAF callback closes over the
   * render it was scheduled in, and the loop needs the value it just wrote.
   */
  const [[winStart, winEnd], setWin] = useState<[number, number]>(initialWindow);
  const winRef = useRef<[number, number]>(initialWindow);

  const winSpan = Math.max(winEnd - winStart, 0.001);
  const pct = useCallback((t: number) => ((t - winStart) / winSpan) * 100, [winStart, winSpan]);

  // Save, not the name field: the common path is confirm-and-resume, and
  // focusing the input would put a keystroke between the coach and Enter.
  useEffect(() => { saveRef.current?.focus(); }, []);

  const commit = useCallback(() => {
    const trimmedNotes = notes.trim() || undefined;
    onSave(start, end, name.trim() || defaultName, trimmedNotes);
  }, [onSave, start, end, name, defaultName, notes]);

  const pointerXRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef(0);

  const stopExtend = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  /**
   * One drag of one handle, whatever the input. `listen` attaches the move and
   * end listeners the input needs and returns what detaches them.
   */
  const beginDrag = useCallback((
    edge: 'start' | 'end',
    clientX: number,
    listen: (move: (x: number) => void, up: () => void) => () => void,
    /** Touch (spec 11.6): the mark's distance from where the finger landed, kept for the drag. */
    grabOffset = 0,
  ) => {
    setDragging(edge);
    pointerXRef.current = clientX;

    /** True while the pointer sits at or past the track edge on the dragged side. */
    const atEdge = (rect: DOMRect) =>
      edge === 'end' ? pointerXRef.current >= rect.right : pointerXRef.current <= rect.left;

    /**
     * Grows the window on the dragged side and drags the handle along with it.
     * The far edge is left alone, so the selection stays on screen and the
     * scale rescales to fit rather than the view sliding off the other end.
     */
    /**
     * Runs for the whole drag, not only while the pointer is moving.
     *
     * This is an autoscroll: the coach holds the handle at the edge and waits,
     * which produces no mousemove at all. Starting the loop from mousemove
     * meant a still pointer never drove it. The loop now owns the decision and
     * simply does nothing on frames where the pointer is inside the track.
     *
     * It also never stops itself. The previous version treated "the window did
     * not change" as "reached the end of the source", which is also true on the
     * priming frame, where dt is 0 by construction — so it died on frame one and
     * only a further mousemove could restart it. Stopping is now the drag's job:
     * mouseup and unmount, nothing else.
     */
    const step = (ts: number) => {
      rafRef.current = requestAnimationFrame(step);

      const dtMs = lastTsRef.current ? ts - lastTsRef.current : 0;
      lastTsRef.current = ts;
      // Priming frame, or a repeated timestamp. Nothing to integrate yet.
      if (dtMs <= 0) return;

      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || !atEdge(rect)) return;

      const delta = EXTEND_RATE * (dtMs / 1000);
      const [lo, hi] = winRef.current;
      const nlo = edge === 'start' ? Math.max(0, lo - delta) : lo;
      const nhi = edge === 'end' ? Math.min(duration, hi + delta) : hi;
      // At the start or the end of the source there is nothing left to give.
      // Keep the loop alive: the pointer may still come back inside, and the
      // other end of the drag is what ends it.
      if (nlo === lo && nhi === hi) return;

      winRef.current = [nlo, nhi];
      setWin([nlo, nhi]);
      // The handle rides the edge it is pinned to, and the seek follows it, so
      // the frame under the pointer keeps updating while the window grows.
      if (edge === 'end') { setEnd(nhi); onSeek(nhi); }
      else { setStart(nlo); onSeek(nlo); }
    };

    const move = (clientX: number) => {
      pointerXRef.current = clientX;
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;

      // At the edge the loop owns the handle, so leave it alone.
      if (atEdge(rect)) return;

      // Read the window from the ref, not from the closure: it may have grown
      // since this handler was created.
      const [lo, hi] = winRef.current;
      const span = Math.max(hi - lo, 0.001);
      const ratio = Math.max(0, Math.min(1, (clientX + grabOffset - rect.left) / rect.width));
      const t = lo + ratio * span;
      if (edge === 'start') {
        // Cannot cross its partner, and cannot leave the window.
        const next = Math.max(lo, Math.min(t, end - MIN_SEGMENT_SECONDS));
        setStart(next);
        onSeek(next);
      } else {
        const next = Math.min(hi, Math.max(t, start + MIN_SEGMENT_SECONDS));
        setEnd(next);
        onSeek(next);
      }
    };
    // The video is left wherever the last seek landed. Restoring the pre-drag
    // position would undo the frame the coach just chose to look at.
    let detach = () => {};
    const up = () => {
      setDragging(null);
      stopExtend();
      detach();
    };
    // The loop belongs to the drag, so it starts with it and ends in `up`.
    lastTsRef.current = 0;
    stopExtend();
    rafRef.current = requestAnimationFrame(step);

    detach = listen(move, up);
  }, [start, end, duration, onSeek, stopExtend]);

  /** A mouse: moves and the release are heard on the document, as always. */
  const handleDragStart = useCallback((edge: 'start' | 'end') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    beginDrag(edge, e.clientX, (move, up) => {
      const mm = (ev: globalThis.MouseEvent) => move(ev.clientX);
      const mu = () => up();
      document.addEventListener('mousemove', mm);
      document.addEventListener('mouseup', mu);
      return () => {
        document.removeEventListener('mousemove', mm);
        document.removeEventListener('mouseup', mu);
      };
    });
  }, [beginDrag]);

  /**
   * A finger or a pen. A finger produces no mousemove while it drags, which is
   * why the handles did not move on a phone. Pointer capture keeps the moves
   * arriving when the finger leaves the handle, and the handle's
   * touch-action: none stops the browser taking the drag as a scroll and
   * cancelling it. Mice are left to onMouseDown, so the desktop drag is the
   * code it always was.
   */
  const handleTouchDragStart = useCallback((edge: 'start' | 'end') => (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    // Also suppresses the mouse events a browser would send after the touch.
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    /* The handle's target can sit to one side of its mark (spec 11.6), so
       the mark follows the finger's movement rather than jumping to it. */
    const track = trackRef.current?.getBoundingClientRect();
    const [lo, hi] = winRef.current;
    const markX = track ? track.left + ((((edge === 'start' ? start : end) - lo) / Math.max(hi - lo, 0.001)) * track.width) : e.clientX;
    beginDrag(edge, e.clientX, (move, up) => {
      const pm = (ev: PointerEvent) => move(ev.clientX);
      const pu = () => up();
      el.addEventListener('pointermove', pm);
      el.addEventListener('pointerup', pu);
      el.addEventListener('pointercancel', pu);
      return () => {
        el.removeEventListener('pointermove', pm);
        el.removeEventListener('pointerup', pu);
        el.removeEventListener('pointercancel', pu);
      };
    }, markX - e.clientX);
  }, [beginDrag, start, end]);

  // A drag can end with the component unmounting, for example on Escape.
  useEffect(() => stopExtend, [stopExtend]);

  return (
    /* Effectively transparent. The scrim exists only to stop clicks reaching
       the editor behind; dimming the video would work against the one thing
       this panel is for, which is watching the frame move while dragging. */
    /* Docked on a phone the scrim leaves the controls bar uncovered, so the
       main scrubber, play and skip still work while the panel is open. It is
       two pieces, above and below the bar; the wrapper itself lets pointers
       through. */
    <div
      className={`fixed inset-0 z-[60] ${docked ? 'pointer-events-none' : ''}`}
      style={{ background: 'transparent' }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      }}
    >
      {docked && bar && (
        <>
          {hole ? (
            /* The part above the bar, less the stage (spec 11.2): above it,
               beside it, and between it and the bar. */
            <>
              <div className="fixed inset-x-0 top-0 pointer-events-auto" style={{ height: Math.max(0, hole.top) }} aria-hidden="true" />
              <div className="fixed left-0 pointer-events-auto" style={{ top: hole.top, width: Math.max(0, hole.left), height: Math.max(0, bar.top - hole.top) }} aria-hidden="true" />
              <div className="fixed right-0 pointer-events-auto" style={{ top: hole.top, left: hole.right, height: Math.max(0, bar.top - hole.top) }} aria-hidden="true" />
              <div className="fixed pointer-events-auto" style={{ top: hole.bottom, left: hole.left, width: Math.max(0, hole.right - hole.left), height: Math.max(0, bar.top - hole.bottom) }} aria-hidden="true" />
            </>
          ) : (
            <div className="fixed inset-x-0 top-0 pointer-events-auto" style={{ height: bar.top }} aria-hidden="true" />
          )}
          <div className="fixed inset-x-0 bottom-0 pointer-events-auto" style={{ top: bar.bottom }} aria-hidden="true" />
        </>
      )}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Confirm captured segment"
        data-testid="segment-capture-modal"
        className={`fixed rounded-xl shadow-xl w-[min(92vw,512px)] ${docked ? 'pointer-events-auto' : ''} ${docked && phoneDock?.landscape ? 'lve-glass' : ''}`}
        style={{
          left: pos?.x ?? 0,
          top: pos?.y ?? 0,
          // Hidden for the first paint only, while the layout effect measures
          // the panel to place it. Without this it flashes at 0,0.
          visibility: pos ? 'visible' : 'hidden',
          ...(docked && phoneDock?.landscape
            ? { borderRadius: 20 }
            : { background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }),
          ...(docked && dockBox ? { width: dockBox.w, maxHeight: dockBox.maxH, overflowY: 'auto' as const } : {}),
        }}
      >
        {/* Drag surface. The only one: the timeline, the field and the buttons
            all sit outside it. */}
        <div
          onPointerDown={handlePanelDragStart}
          /* Compact on a phone, about 250 in all, so it fits the SE's plan
             region (268) without scrolling. */
          className={`px-6 pt-4 pb-2 phone:px-4 phone:pt-3 rounded-t-xl select-none ${docked ? '' : movingPanel ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          <h3 className="text-base font-semibold" style={{ color: 'var(--editor-text)' }}>
            New segment
          </h3>
          <p className="text-xs mt-1" style={{ color: 'var(--editor-text-muted)' }}>
            Drag either end to extend or trim it.
          </p>
        </div>

        <div className="px-6 pb-4 phone:px-4 phone:pb-3">
          {/* Local timeline */}
          <div
            ref={trackRef}
            className="relative h-10 phone:h-11 rounded-lg select-none"
            style={{ background: 'var(--editor-bg)', border: '1px solid var(--editor-border)' }}
          >
            <div
              className="absolute top-0 bottom-0 rounded-md"
              style={{
                left: `${pct(start)}%`,
                width: `${pct(end) - pct(start)}%`,
                background: 'var(--color-biscayne)',
              }}
            />
            {(['start', 'end'] as const).map((edge) => {
              const t = edge === 'start' ? start : end;
              /* Phone (spec 11.6): when the two marks are closer than a
                 44 target, each handle's target opens outward from its mark,
                 so both stay grabbable and the range shows between them. */
              const apart = docked && trackW > 0 && ((pct(end) - pct(start)) / 100) * trackW < 44;
              const outward = apart ? (edge === 'start' ? { marginLeft: -44, justifyContent: 'flex-end' } : { marginLeft: 0, justifyContent: 'flex-start' }) : {};
              return (
                <div
                  key={edge}
                  role="slider"
                  tabIndex={-1}
                  aria-label={edge === 'start' ? 'Segment start' : 'Segment end'}
                  aria-valuemin={winStart}
                  aria-valuemax={winEnd}
                  aria-valuenow={t}
                  data-testid={`capture-handle-${edge}`}
                  onMouseDown={handleDragStart(edge)}
                  onPointerDown={handleTouchDragStart(edge)}
                  /* 24 wide of hit target around a 6 wide visual, per 8.4's rule
                     that the target is expanded rather than the mark inflated.
                     44 by 44 on a phone: the track is 44 with a 1px border, so
                     the handle reaches over it top and bottom. */
                  className="absolute top-0 bottom-0 w-6 -ml-3 phone:w-11 phone:-ml-[22px] phone:-top-px phone:-bottom-px flex items-center justify-center cursor-col-resize"
                  style={{ left: `${pct(t)}%`, touchAction: 'none', ...outward }}
                >
                  <span
                    className="h-full w-1.5 rounded-sm flex flex-col items-center justify-center gap-[3px]"
                    style={{
                      background: dragging === edge
                        ? 'var(--editor-text)'
                        : 'var(--color-db-oasis)',
                    }}
                  >
                    {/* Grip, so it reads as something to take hold of. */}
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="block w-[3px] h-[3px] rounded-full"
                        style={{ background: 'var(--editor-bg)' }}
                      />
                    ))}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Readout. The range and its duration are what the coach is moving,
              so they carry the weight; the window bounds are context. */}
          {docked ? (
            /* Phone (spec 11.6): one line, "0:56 to 0:59 · 3s", so it fits
               the landscape panel. */
            <div className="mt-1.5 flex justify-center">
              <span className="w-max text-[13px] leading-[18px] tabular-nums font-semibold" style={{ color: 'var(--editor-text)' }} data-testid="capture-duration">
                {/* Rounded alike, so the times and the length always agree. */}
                {formatTime(Math.round(start))} to {formatTime(Math.round(end))}
                <span style={{ color: 'var(--color-db-oasis)' }}> · {Math.round(end) - Math.round(start) >= 1 ? `${Math.round(end) - Math.round(start)}s` : `${(end - start).toFixed(1)}s`}</span>
              </span>
            </div>
          ) : (
          <div className="mt-1.5 flex items-baseline justify-between gap-3">
            <span className="text-[11px] tabular-nums" style={{ color: 'var(--editor-text-muted)', opacity: 0.7 }}>
              {formatTime(winStart)}
            </span>
            <span className="text-[13px] tabular-nums font-semibold" style={{ color: 'var(--editor-text)' }} data-testid="capture-duration">
              {formatTime(start)} to {formatTime(end)}
              <span className="phone:inline-block" style={{ color: 'var(--color-db-oasis)' }}> · {formatDuration(end - start)}</span>
            </span>
            <span className="text-[11px] tabular-nums" style={{ color: 'var(--editor-text-muted)', opacity: 0.7 }}>
              {formatTime(winEnd)}
            </span>
          </div>
          )}

          {/* Name. No label: the field opens prefilled with the default name,
              so it says what it is. The aria-label carries the accessible name
              the visible one used to. */}
          <div className="mt-3 phone:mt-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Segment name"
              data-testid="capture-name-input"
              className="w-full px-3 py-2 phone:h-11 rounded-lg text-[16px] leading-[21px] focus:ring-2 focus:ring-db-accent focus:border-transparent input-glow"
              style={{ background: 'var(--editor-surface)', border: '1px solid var(--editor-border)', color: 'var(--editor-text)' }}
              placeholder={defaultName}
            />
          </div>
          <div className="mt-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              aria-label="Segment notes"
              rows={2}
              className="w-full px-3 py-2 rounded-lg text-[13px] leading-[18px] resize-none focus:ring-2 focus:ring-db-accent focus:border-transparent"
              style={{ background: 'var(--editor-surface)', border: '1px solid var(--editor-border)', color: 'var(--editor-text)' }}
              placeholder="Notes (optional)"
            />
          </div>

          {/* 48 high, radius 100, equal width, 12px apart. Concrete values
              rather than buttonStyles: the editor has no shared recipe and
              importing the light-theme one would drag in its colours. */}
          <div className="flex gap-3 mt-3">
            <button
              onClick={onCancel}
              className="flex-1 min-w-0 min-h-12 px-3 rounded-[100px] text-[15px] leading-[20px] font-medium transition-colors"
              style={{ background: 'transparent', border: '1px solid var(--editor-border)', color: 'var(--editor-text-muted)' }}
            >
              Discard
            </button>
            <button
              ref={saveRef}
              data-testid="confirm-segment-btn"
              onClick={commit}
              className="flex-1 min-w-0 min-h-12 px-3 rounded-[100px] text-[15px] leading-[20px] font-medium text-white transition-colors btn-press"
              style={{ background: 'var(--color-biscayne)' }}
            >
              {/* Phone (spec 11.6): "Save", so the row never wraps in the landscape panel. */}
              {docked ? 'Save' : 'Save Segment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
